import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { Tooltip, TooltipContent, TooltipTrigger } from "@/components/ui/tooltip";
import { usePermission } from "@/hooks/use-premission";
import {
  useAssignPlan,
  useGetActiveSubscriptionPlans,
  useUnassignPlan,
} from "@/hooks/use-subscription";
import {
  useAssignB2cSenderPlan,
  useUnassignB2cSenderPlan,
} from "@/hooks/use-b2c-sender";
import { cn, extractErrorMessage } from "@/lib/utils";
import { MailboxType, TargetAudience } from "@/types/mailbox.types";
import { SubscriptionStatus, ESPType } from "@/types/subscription.types";
import { QueryClient, useQueryClient } from "@tanstack/react-query";
import { Check, ChevronDown, Loader2, X } from "lucide-react";
import { useEffect, useMemo, useState } from "react";
import { toast } from "sonner";
import { authProviderIcons } from "@/pages/mailbox/esp-providers";

type Props = {
  mailboxId: string;
  mailboxEmail: string;
  subscriptionId: string | null;
  planId: string | null;
  addonPlanId: string | null;
  pageType: "list" | "details";
  className?: string;
  targetAudience?: TargetAudience | string; // Add optional target audience prop
  isSenderProfile?: boolean; // Indicates if this is for sender profiles (seed list plans) or mailboxes (B2B/B2C plans)
  onSelect?: (
    type: "assign" | "unassign",
    data: { subscriptionId: string; planId: string }
  ) => void;
};

type SelectedPlanMetaData = {
  subscriptionId: string | null;
  planId: string | null;
};

type PlanOption = {
  key: string;
  label: string;
  value: string;
  type: "custom" | "regular";
  category?: string;
  planType?: string;
  emailsPerDay?: number;
  subscriptionId: string;
  status: SubscriptionStatus;
  interval: string;
  used: number;
  total: number;
  isSeedListPlan?: boolean;
  espTypes?: ESPType[];
  seedCountPerSender?: number;
};

const AssignSubscriptionSelect = (props: Props) => {
  const { data: subscriptionPlans } = useGetActiveSubscriptionPlans();
  const [selectedPlanMetaData, setSelectedPlanMetaData] =
    useState<SelectedPlanMetaData | null>(null);

  const queryClient = useQueryClient();

  const { hasPermission } = usePermission();

  // Use different hooks based on whether this is for sender profiles or mailboxes
  const { mutateAsync: unassignPlan, isPending: isUnassigning } =
    useUnassignPlan();
  const { mutateAsync: assignPlan, isPending: isAssigning } = useAssignPlan();
  
  const { mutateAsync: assignB2cSenderPlan, isPending: isAssigningB2cSender } =
    useAssignB2cSenderPlan();
  const { mutateAsync: unassignB2cSenderPlan, isPending: isUnassigningB2cSender } =
    useUnassignB2cSenderPlan();

  // Helper function to get ESP type icon
  const getEspTypeIcon = (espType: string) => {
    const espTypeStr = String(espType).trim().toLowerCase();
    const normalizedType = espTypeStr.replace(/[\s-]+/g, "_");
    return authProviderIcons[normalizedType] 
      || authProviderIcons[espTypeStr]
      || authProviderIcons[espTypeStr.replace(/_/g, "-")]
      || null;
  };

  // Helper function to format ESP type name for display
  const formatEspTypeName = (espType: string): string => {
    const espTypeStr = String(espType).trim();
    // Map ESP types to display names
    const displayNames: Record<string, string> = {
      "Gmail": "Gmail",
      "Outlook": "Outlook",
      "Yahoo": "Yahoo",
      "Google Workspace": "Google Workspace",
      "Microsoft 365": "Microsoft 365",
      "AOL": "AOL",
      "iCloud": "iCloud",
    };
    return displayNames[espTypeStr] || espTypeStr;
  };

  // Helper function to generate label for seed list plans
  const generateSeedListLabel = (espTypes: ESPType[], seedCountPerSender: number): string => {
    if (!espTypes || espTypes.length === 0) {
      return "Seed List";
    }
    
    const formattedEspTypes = espTypes.map(formatEspTypeName).join(", ");
    return `${formattedEspTypes} (${seedCountPerSender.toLocaleString()} seeds)`;
  };

  const options = useMemo<PlanOption[]>(() => {
    if (!subscriptionPlans) return [];

    // Flatten the nested structure and filter out null values
    const allOptions: PlanOption[] = subscriptionPlans
      .flatMap((subscription) =>
        subscription.plans.map((plan): PlanOption | null => {
          // Handle custom plans
          if (plan?.isCustomPlan && plan?.addonPlanId?._id) {
            return {
              key: `${subscription._id}-${plan.addonPlanId._id}`,
              label: `Custom Plan (${plan.addonPlanId.maxEmailsPerDay} emails/day)`,
              value: plan.addonPlanId._id,
              type: "custom" as const,
              emailsPerDay: plan.addonPlanId.maxEmailsPerDay,
              subscriptionId: subscription._id,
              status: subscription.status,
              interval: subscription.interval,
              used: plan.assignedMailboxes?.length || 0,
              total: plan.quantity,
              category: "B2C", // Default to B2C for custom plans
              isSeedListPlan: false,
              espTypes: [],
            };
          }

          // Handle regular plans
          if (plan?.planId?._id) {
            // Check if this is a seed list subscription (sender profile plan)
            const isSeedListPlan = plan.planId.isOnDemandSenderProfile || false;
            const espTypes = plan.planId.espTypes || plan.planId.senderProfileMetadata?.espTypes || [];
            const seedCountPerSender = plan.planId.seedCountPerSender || plan.planId.senderProfileMetadata?.baseSeedCount || 0;
            
            // Generate label based on plan type
            let label: string;
            if (isSeedListPlan && espTypes.length > 0) {
              // For seed list plans, show ESP types and seed count
              label = generateSeedListLabel(espTypes as ESPType[], seedCountPerSender);
            } else {
              // For regular plans, show category and plan type
              label = `${plan.planId.category} - ${plan.planId.planType}`;
            }
            
            // For seed list plans, use usedSenderProfilesMonthly and maxSenderProfilesMonthly
            // For regular plans, use assignedMailboxes.length and quantity
            const used = isSeedListPlan
              ? (plan.usedSenderProfilesMonthly || 0)
              : (plan.assignedMailboxes?.length || 0);
            const total = isSeedListPlan
              ? (plan.maxSenderProfilesMonthly || plan.quantity || 0)
              : plan.quantity;
            
            return {
              key: `${subscription._id}-${plan.planId._id}`,
              label,
              value: plan.planId._id,
              type: "regular" as const,
              category: plan.planId.category,
              planType: plan.planId.planType,
              subscriptionId: subscription._id,
              status: subscription.status,
              interval: subscription.interval,
              used,
              total,
              isSeedListPlan,
              espTypes: espTypes as ESPType[],
              seedCountPerSender,
            };
          }

          return null;
        })
      )
      .filter((option): option is PlanOption => option !== null);

    // Filter based on context (sender profiles vs mailboxes)
    let filteredOptions = allOptions;
    
    if (props.isSenderProfile) {
      // For sender profiles: only show seed list plans
      filteredOptions = allOptions.filter((option) => option.isSeedListPlan === true);
    } else {
      // For mailboxes: only show B2B and B2C plans (exclude seed list plans)
      filteredOptions = allOptions.filter((option) => {
        if (option.isSeedListPlan) {
          return false; // Exclude seed list plans
        }
        const category = option.category?.toUpperCase() || "";
        return category === "B2B" || category === "B2C";
      });
    }

    // Filter by target audience if provided (only applies to mailboxes)
    if (props.targetAudience && !props.isSenderProfile) {
      const audience = props.targetAudience.toLowerCase();
      if (audience === "b2b" || audience === "b2c") {
        return filteredOptions.filter((option) => {
          const optionCategory = option.category?.toLowerCase() || "b2c";
          return optionCategory === audience;
        });
      }
      // If targetAudience is "both", show all filtered options
      return filteredOptions;
    }

    return filteredOptions;
  }, [subscriptionPlans, props.targetAudience, props.isSenderProfile]);

  const selectedOption = options?.find(
    (option) => option.value === selectedPlanMetaData?.planId
  );

  const handleUnassign = async () => {
    const currentAssignedPlanId = props.planId || props.addonPlanId;

    if (!props.subscriptionId || !currentAssignedPlanId) {
      toast.error(
        "No subscription to unassign, please assign a subscription first"
      );
      return;
    }

    try {
      if (props.isSenderProfile) {
        // Use B2C sender unassign endpoint
        await unassignB2cSenderPlan(props.mailboxId);
        setSelectedPlanMetaData(null);
        queryClient.invalidateQueries({ queryKey: ["warmupAccounts"] });
        queryClient.invalidateQueries({ queryKey: ["b2cSenders"] });
        window.cioanalytics?.track("sender_profile_subscription_unassigned", {
          subscriptionId: props.subscriptionId,
          planId: currentAssignedPlanId,
          email: props.mailboxEmail,
        });
      } else {
        // Use mailbox unassign endpoint
        const updatedMailbox = await unassignPlan({
          subscriptionId: props.subscriptionId,
          mailboxId: props.mailboxId,
          planId: currentAssignedPlanId,
        });

        setSelectedPlanMetaData(null);
        optimisticUpdate(
          queryClient,
          "unassign",
          props.pageType,
          updatedMailbox,
          {
            mailboxId: props.mailboxId,
            subscriptionId: props.subscriptionId,
            planId: currentAssignedPlanId,
          }
        );

        toast.success("Mailbox unassigned successfully");
        window.cioanalytics?.track("mailbox_subscription_ unassigned", {
          subscriptionId: props.subscriptionId,
          planId: currentAssignedPlanId,
          email: props.mailboxEmail,
        });
      }
    } catch (error) {
      const message = extractErrorMessage(error, "Failed to unassign plan.");
      toast.error(message);
    }
  };

  const handleAssign = async ({
    subscriptionId,
    planId,
  }: {
    subscriptionId: string;
    planId: string;
  }) => {
    try {
      if (!subscriptionId || !planId) {
        toast.error("Provide a valid subscription and plan");
        return;
      }

      if (props.isSenderProfile) {
        // Use B2C sender assign endpoint: POST /b2c-sender/:id/assign-plan
        await assignB2cSenderPlan({
          id: props.mailboxId,
          planId,
          subscriptionId,
        });

        setSelectedPlanMetaData({
          subscriptionId,
          planId,
        });

        queryClient.invalidateQueries({ queryKey: ["warmupAccounts"] });
        queryClient.invalidateQueries({ queryKey: ["b2cSenders"] });
        queryClient.invalidateQueries({ queryKey: ["b2cSender", props.mailboxId] });

        window.cioanalytics?.track("sender_profile_subscription_assigned", {
          subscriptionId,
          planId,
          email: props.mailboxEmail,
        });
      } else {
        // Use mailbox assign endpoint
        const updatedMailbox = await assignPlan({
          subscriptionId,
          mailboxId: props.mailboxId,
          planId,
        });

        setSelectedPlanMetaData({
          subscriptionId,
          planId,
        });

        optimisticUpdate(queryClient, "assign", props.pageType, updatedMailbox, {
          mailboxId: props.mailboxId,
          subscriptionId,
          planId,
        });

        toast.success("Mailbox assigned successfully");
        window.cioanalytics?.track("mailbox_subscription_assigned", {
          subscriptionId,
          planId,
          email: props.mailboxEmail,
        });
      }
    } catch (error) {
      const message = extractErrorMessage(error, "Failed to assign plan.");
      toast.error(message);
    }
  };

  useEffect(() => {
    setSelectedPlanMetaData({
      subscriptionId: props.subscriptionId,
      planId: props.planId || props.addonPlanId,
    });
  }, [props.subscriptionId, props.planId, props.addonPlanId]);

  const isLoading = props.isSenderProfile
    ? isUnassigningB2cSender || isAssigningB2cSender
    : isUnassigning || isAssigning;

  if (!hasPermission("mailboxes:edit")) {
    return (
      <div className="flex items-center gap-2 text-sm text-muted-foreground">
        <span>
          {selectedOption
            ? `${selectedOption?.label} (${selectedOption?.interval})`
            : "No subscription assigned"}
        </span>
        {selectedOption?.isSeedListPlan && selectedOption.espTypes && selectedOption.espTypes.length > 0 && (
          <div className="flex items-center gap-1">
            {selectedOption.espTypes.map((espType, index) => {
              const iconMapping = getEspTypeIcon(espType);
              if (!iconMapping) return null;
              return (
                <Tooltip key={index}>
                  <TooltipTrigger asChild>
                    <div className="flex items-center">
                      {iconMapping.icon}
                    </div>
                  </TooltipTrigger>
                  <TooltipContent>
                    {iconMapping.tooltip}
                  </TooltipContent>
                </Tooltip>
              );
            })}
          </div>
        )}
      </div>
    );
  }
  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <Button
          variant="outline"
          className={cn("w-full justify-between", props.className)}
          disabled={isLoading}
        >
          <div className="flex items-center gap-2 flex-1 min-w-0">
            <span className="text-left truncate">
              {selectedOption
                ? `${selectedOption?.label} (${selectedOption?.interval})`
                : "Select Subscription"}
            </span>
            {selectedOption?.isSeedListPlan && selectedOption.espTypes && selectedOption.espTypes.length > 0 && (
              <div className="flex items-center gap-1 flex-shrink-0">
                {selectedOption.espTypes.map((espType, index) => {
                  const iconMapping = getEspTypeIcon(espType);
                  if (!iconMapping) return null;
                  return (
                    <Tooltip key={index}>
                      <TooltipTrigger asChild>
                        <div className="flex items-center">
                          {iconMapping.icon}
                        </div>
                      </TooltipTrigger>
                      <TooltipContent>
                        {iconMapping.tooltip}
                      </TooltipContent>
                    </Tooltip>
                  );
                })}
              </div>
            )}
          </div>
          {isLoading ? (
            <Loader2 className="w-4 h-4 ml-2 animate-spin flex-shrink-0" />
          ) : (
            <ChevronDown className="w-4 h-4 ml-2 flex-shrink-0" />
          )}
        </Button>
      </DropdownMenuTrigger>
      <DropdownMenuContent 
        className="min-w-[var(--radix-dropdown-menu-trigger-width)] max-w-[calc(100vw-2rem)] max-h-[400px] overflow-y-auto"
        align="start"
        side="bottom"
        sideOffset={4}
      >
        {options?.map((option) => (
          <DropdownMenuItem
            key={option.key}
            onSelect={() =>
              handleAssign({
                subscriptionId: option.subscriptionId,
                planId: option.value,
              })
            }
            className="flex flex-col gap-0 items-start hover:bg-accent cursor-pointer"
            disabled={
              selectedOption?.value === option.value ||
              option.status === SubscriptionStatus.PENDING
            }
          >
            <div className="flex items-center justify-between w-full">
              <div className="flex items-center gap-2 flex-1 min-w-0">
                <span className="font-medium truncate">{option.label}</span>
                {option.isSeedListPlan && option.espTypes && option.espTypes.length > 0 && (
                  <div className="flex items-center gap-1 flex-shrink-0">
                    {option.espTypes.map((espType, index) => {
                      const iconMapping = getEspTypeIcon(espType);
                      if (!iconMapping) return null;
                      return (
                        <Tooltip key={index}>
                          <TooltipTrigger asChild>
                            <div className="flex items-center">
                              {iconMapping.icon}
                            </div>
                          </TooltipTrigger>
                          <TooltipContent>
                            {iconMapping.tooltip}
                          </TooltipContent>
                        </Tooltip>
                      );
                    })}
                  </div>
                )}
              </div>
              {selectedOption?.value === option.value && (
                <Check className="w-4 h-4 flex-shrink-0" />
              )}
            </div>
            <div className="flex items-center gap-1 text-xs text-muted-foreground">
              <span>{option.interval}</span>
              <span>•</span>
              <span>{option.total - option.used} remaining</span>
              {option.status === SubscriptionStatus.PENDING && (
                <Badge
                  variant="default"
                  className="text-xs ml-2 bg-red-100 text-red-500 border-none shadow-none"
                >
                  Unpaid
                </Badge>
              )}
            </div>
          </DropdownMenuItem>
        ))}
        {options?.length === 0 && (
          <DropdownMenuItem disabled>
            <span className="text-gray-500">No options available</span>
          </DropdownMenuItem>
        )}

        {/* Unassign Subscription */}
        {selectedOption && (
          <>
            <DropdownMenuSeparator />
            <DropdownMenuItem
              onSelect={() => handleUnassign()}
              className="justify-between text-destructive hover:!text-destructive hover:!bg-destructive/10 cursor-pointer"
            >
              <span>Unassign Subscription</span>
              <X className="w-4 h-4 ml-2" />
            </DropdownMenuItem>
          </>
        )}
      </DropdownMenuContent>
    </DropdownMenu>
  );
};

const optimisticUpdate = (
  queryClient: QueryClient,
  type: "unassign" | "assign",
  pageType: "list" | "details",
  data: MailboxType,
  variables: {
    mailboxId: string;
    subscriptionId: string;
    planId: string;
  }
) => {
  if (pageType === "list") {
    try {
      const activePageQueries = queryClient
        .getQueryCache()
        .findAll({ queryKey: ["mailboxes"] }) // match all pagination queries
        .filter((query) => query.isActive());

      if (activePageQueries.length === 0) {
        throw new Error("No active page queries found");
      }

      const activePageQuery = activePageQueries[0];

      const oldData = activePageQuery.state.data as {
        data: MailboxType[];
        total: number;
      };

      const updatedData = oldData.data.map((mailbox: MailboxType) => {
        if (mailbox._id === variables.mailboxId) {
          return {
            ...mailbox,
            subscriptionId: data.subscriptionId,
            planId: data.planId,
            addonPlan: data.addonPlan,
          };
        }
        return mailbox;
      });

      const newData = {
        ...oldData,
        data: updatedData,
      };

      activePageQuery.state.data = newData;
    } catch (error) {
      console.error("failed to optimistic update, invalidating queries");
      queryClient.invalidateQueries({
        queryKey: ["mailboxes"],
      });
    }
  } else {
    queryClient.invalidateQueries({
      queryKey: ["mailboxes", { id: variables.mailboxId }],
    });

    // queryClient.setQueryData(
    //   ["mailboxes", { id: variables.mailboxId }],
    //   (oldData: MailboxType) => {
    //     const update =
    //       type === "unassign"
    //         ? {
    //             subscriptionId: null,
    //           }
    //         : {
    //             subscriptionId: variables.subscriptionId,
    //           };
    //     return Object.assign({}, oldData, update);
    //   }
    // );
  }
};

export default AssignSubscriptionSelect;
